import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from '../api/client';
import type { AuthUser, SiteSummary } from '../types';

const CURRENT_SITE_KEY = 'dmt_dashboard_site';

interface AuthContextValue {
  user: AuthUser | null;
  sites: SiteSummary[];
  currentSiteId: string | null;
  setCurrentSiteId: (siteId: string) => void;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [currentSiteId, setCurrentSiteIdState] = useState<string | null>(() => localStorage.getItem(CURRENT_SITE_KEY));
  const [isLoading, setIsLoading] = useState(true);

  const loadSites = useCallback(async (isSuperAdmin: boolean, grantedSites: SiteSummary[] | null) => {
    const resolved = isSuperAdmin ? (await api.listSites()).sites : (grantedSites ?? []);
    setSites(resolved);
    return resolved;
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    api
      .me()
      .then(async ({ user: me, sites: grantedSites }) => {
        setUser(me);
        const resolved = await loadSites(me.isSuperAdmin, grantedSites);
        setCurrentSiteIdState((prev) => prev ?? resolved[0]?.id ?? null);
      })
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, [loadSites]);

  const setCurrentSiteId = useCallback((siteId: string) => {
    localStorage.setItem(CURRENT_SITE_KEY, siteId);
    setCurrentSiteIdState(siteId);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const { token, user: loggedInUser, sites: grantedSites } = await api.login(email, password);
      setToken(token);
      setUser(loggedInUser);
      const resolved = await loadSites(loggedInUser.isSuperAdmin, grantedSites);
      if (resolved[0]) setCurrentSiteId(resolved[0].id);
    },
    [loadSites, setCurrentSiteId],
  );

  const logout = useCallback(() => {
    clearToken();
    localStorage.removeItem(CURRENT_SITE_KEY);
    setUser(null);
    setSites([]);
    setCurrentSiteIdState(null);
  }, []);

  const value = useMemo(
    () => ({ user, sites, currentSiteId, setCurrentSiteId, isLoading, login, logout }),
    [user, sites, currentSiteId, setCurrentSiteId, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
